/**
 * Black Matte VTC - Partner Dashboard Overview
 * Pure view layer - ALL data from API
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { partnerService } from '../../services/partnerService';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Plus,
  TrendingUp,
  CreditCard,
  Calendar,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

// Status badge component - pure display, no logic
const BookingStatusBadge = ({ status }) => {
  const statusConfig = {
    confirmed: { label: 'Confirmed', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    completed: { label: 'Completed', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    cancelled: { label: 'Cancelled', className: 'bg-red-500/10 text-red-400 border-red-500/20' },
    in_progress: { label: 'In Progress', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    pending: { label: 'Pending', className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20' },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

const PartnerDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch ALL data from API - no local calculations
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboard, balance] = await Promise.all([
          partnerService.getDashboard(),
          partnerService.getBalance()
        ]);
        setDashboardData(dashboard);
        setBalanceData(balance);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ALL values come from API - not calculated
  const isBlocked = balanceData?.has_overdue_invoices || !balanceData?.booking_allowed;

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      {/* Header with action */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Welcome back, {dashboardData?.partner_name}
          </h1>
          <p className="text-neutral-500">
            {dashboardData?.partner_type} Account
          </p>
        </div>
        
        <Button 
          asChild
          className="bg-white text-black hover:bg-neutral-200"
          disabled={isBlocked}
          data-testid="new-booking-btn"
        >
          <Link to="/partner/bookings/new">
            <Plus className="h-4 w-4 mr-2" />
            New Booking
          </Link>
        </Button>
      </div>

      {/* Blocking Banner - Only shown if API says blocked */}
      {isBlocked && (
        <div 
          className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3"
          data-testid="blocked-banner"
        >
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-400">Booking Temporarily Unavailable</p>
            <p className="text-xs text-red-400/80 mt-1">
              {balanceData?.has_overdue_invoices
                ? 'You have overdue invoices. Please settle them to continue booking.'
                : 'Credit limit reached. Please make a payment to continue.'}
            </p>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            asChild
          >
            <Link to={balanceData?.has_overdue_invoices ? '/partner/invoices' : '/partner/payments'}>
              {balanceData?.has_overdue_invoices ? 'View Invoices' : 'Make Payment'}
            </Link>
          </Button>
        </div>
      )}

      {/* Stats Grid - ALL values from API */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Current Balance */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-neutral-400">
              Current Balance
            </CardTitle>
            <CreditCard className="h-4 w-4 text-neutral-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white" data-testid="current-balance">
              €{dashboardData?.current_balance?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              Credit: €{balanceData?.credit_available?.toFixed(0) || '0'} available
            </p>
          </CardContent>
        </Card>

        {/* Total Spent */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-neutral-400">
              Total Spent
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-neutral-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white" data-testid="total-spent">
              €{dashboardData?.total_spent?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              All time
            </p>
          </CardContent>
        </Card>

        {/* Total Bookings */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-neutral-400">
              Total Bookings
            </CardTitle>
            <Calendar className="h-4 w-4 text-neutral-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white" data-testid="total-bookings">
              {dashboardData?.total_bookings || 0}
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              {dashboardData?.bookings_this_month || 0} this month
            </p>
          </CardContent>
        </Card>

        {/* Credit Limit */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-neutral-400">
              Credit Limit
            </CardTitle>
            {balanceData?.booking_allowed ? (
              <CheckCircle className="h-4 w-4 text-emerald-500" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white" data-testid="credit-limit">
              €{dashboardData?.credit_limit?.toFixed(0) || '0'}
            </div>
            <div className="mt-2">
              {/* Progress bar - visual only, values from API */}
              <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    balanceData?.booking_allowed ? 'bg-emerald-500' : 'bg-red-500'
                  }`}
                  style={{ 
                    width: `${Math.min(100, ((dashboardData?.current_balance || 0) / (dashboardData?.credit_limit || 1)) * 100)}%` 
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Bookings - Data from API */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">Recent Bookings</CardTitle>
          <Button variant="ghost" size="sm" asChild className="text-neutral-400 hover:text-white">
            <Link to="/partner/bookings">
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {dashboardData?.last_10_bookings?.length > 0 ? (
            <div className="space-y-3" data-testid="recent-bookings">
              {dashboardData.last_10_bookings.slice(0, 5).map((booking) => (
                <Link
                  key={booking.id}
                  to={`/partner/bookings/${booking.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">
                        {booking.booking_reference}
                      </span>
                      <BookingStatusBadge status={booking.status} />
                    </div>
                    <p className="text-xs text-neutral-500 truncate mt-1">
                      {booking.pickup_address} → {booking.destination_address}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm font-medium text-white">
                      €{booking.estimated_price?.toFixed(2) || '—'}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {booking.date} {booking.time}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-neutral-700 mx-auto mb-3" />
              <p className="text-neutral-500">No bookings yet</p>
              <Button 
                asChild 
                size="sm" 
                className="mt-4 bg-white text-black hover:bg-neutral-200"
                disabled={isBlocked}
              >
                <Link to="/partner/bookings/new">Create First Booking</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Loading skeleton
const DashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <div>
        <Skeleton className="h-8 w-48 bg-neutral-800" />
        <Skeleton className="h-4 w-24 bg-neutral-800 mt-2" />
      </div>
      <Skeleton className="h-10 w-32 bg-neutral-800" />
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="bg-neutral-900 border-neutral-800">
          <CardHeader className="pb-2">
            <Skeleton className="h-4 w-24 bg-neutral-800" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-8 w-20 bg-neutral-800" />
            <Skeleton className="h-3 w-32 bg-neutral-800 mt-2" />
          </CardContent>
        </Card>
      ))}
    </div>
    
    <Card className="bg-neutral-900 border-neutral-800">
      <CardHeader>
        <Skeleton className="h-6 w-36 bg-neutral-800" />
      </CardHeader>
      <CardContent className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full bg-neutral-800" />
        ))}
      </CardContent>
    </Card>
  </div>
);

export default PartnerDashboard;
